/**
   * API Client for Gaming CRM
   * Migrated to Go-API with JWT authentication
   *
   * IMPORTANT: This requires a valid JWT token
   * Use login() method to authenticate and get JWT token
   */

// Helper function to get cookie value
function getCookie(name: string): string | null {
  if (typeof window === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

class APIClient {
  private server_domain: string;
  private api_domain: string;
  private token: string | null = null;

  constructor() {
    // Load server configuration
    // Remove trailing slash to prevent double slashes
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1';
    this.server_domain = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    this.api_domain = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

    //console.log('[API CLIENT] Initializing with URL:', apiUrl);
    //console.log('[API CLIENT] Server domain:', this.server_domain);

    // Initialize JWT token from cookie first, then sessionStorage
    if (typeof window !== 'undefined') {
      this.token = getCookie('auth_token') || sessionStorage.getItem('auth_token');
      /*console.log('[API CLIENT] Initial auth state:', {
        hasToken: !!this.token
      });*/
    }
  }

  /**
   * Standard API call method using Go-API pattern
   * @param module Module name (e.g., 'merchants', 'customers', 'fleet_assets')
   * @param method Method name (e.g., 'create', 'read', 'update', 'delete', 'list')
   * @param formData Data to send to the module
   * @returns Promise with the API response
   */
  async callApi(module: string, method: string, formData: Record<string, unknown> = {}) {
    try {
      // Check authentication - if not set, try to get from cookie first, then sessionStorage
      if (!this.token) {
        //console.log('[API DEBUG] No token in memory, checking cookie/sessionStorage...');
        if (typeof window !== 'undefined') {
          this.token = getCookie('auth_token') || sessionStorage.getItem('auth_token');
          //console.log('[API DEBUG] Retrieved from storage:');
          //console.log('  - hasToken:', !!this.token);
          //console.log('  - hasUserData:', !!sessionStorage.getItem('user_data'));
        }

        // Throw error if no token available
        if (!this.token) {
          console.error('[API ERROR] Authentication required - JWT token not found');
          throw new Error("Authentication required - JWT token not found");
        }
      }

      // Create request ID for tracking
      const requestId = `${module}_${method}_${Date.now()}`;
      const apiUrl = `${this.server_domain}/${module}.${method}`;

      // Prepare request body with method and data
      const requestBody = {
        method: `${module}.${method}`,
        ...formData
      };

      //console.log(`--> POST ${apiUrl} [${requestId}]`);
      //console.log(JSON.stringify(requestBody));

      // Make the API call with JWT Bearer token
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.token}`
        },
        body: JSON.stringify(requestBody)
      });

      // Handle error responses - extract error message from backend
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // If parsing fails, use default error message
        }

        // Handle 401 Unauthorized - Invalid token
        if (response.status === 401 && errorMessage === 'Invalid token') {
          console.error('[API ERROR] Invalid token - clearing session and logging out');

          // Clear token from memory
          this.token = null;

          // Clear session storage
          if (typeof window !== 'undefined') {
            sessionStorage.clear();

            // Clear auth cookie
            document.cookie = 'auth_token=; path=/; max-age=0';

            // Clear any other auth-related cookies
            document.cookie = 'user_data=; path=/; max-age=0';

            // Redirect to login page
            window.location.href = '/login';
          }

          // Throw error to stop further processing
          const error = new Error('Session expired. Please log in again.') as any;
          error.status = 401;
          throw error;
        }

        const error = new Error(errorMessage) as any;
        error.status = response.status;
        throw error;
      }

      const responseData = await response.json();
      //console.log(`<-- Response [${requestId}]`, responseData);

      // Handle multiple response formats from Go-API:
      // 1. {success: true, data: {...}} - standard format
      // 2. {data} - direct format (auth endpoints)
      // 3. Any other format - return as-is
      if (responseData && responseData.success === true && 'data' in responseData) {
        return responseData.data;
      }

      // For endpoints that return data directly at root (like /authenticate)
      // just return the response as-is since it already has the structure we need
      return responseData;

    } catch (error) {
      console.error('API Call Error:', error);
      throw error;
    }
  }

  /**
   * Set authentication JWT token
   * @param token JWT token
   */
  setAuth(token: string) {
    this.token = token;

    // Store in both cookie and sessionStorage
    if (typeof window !== 'undefined') {
      const maxAge = 7 * 24 * 60 * 60;
      const isSecure = window.location.protocol === 'https:';
      document.cookie = `auth_token=${token}; path=/; max-age=${maxAge}; SameSite=Lax${isSecure ? '; Secure' : ''}`;
      sessionStorage.setItem('auth_token', token);
    }
  }

  /**
   * Login and get JWT token
   * @param email User email
   * @param password User password
   * @returns Promise with login result
   */
  async login(email: string, password: string) {
    try {
      // Use the Go-API authenticate endpoint for login
      const apiUrl = `${this.server_domain}/authenticate`;
      const loginData = {
        action: 'login',
        email: email,
        password: password
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      // Check if login was successful
      // Go-API returns data directly at root level: {token, user, merchant, ...}
      if (responseData.token && responseData.user) {
        const { token, user } = responseData;

        // Set authentication with JWT token
        this.setAuth(token);

        //console.log('[API DEBUG] Login successful, user ID:', user.id);
        //console.log('[API DEBUG] JWT token set:', token.substring(0, 10) + '...');

        return {
          success: true,
          user: user,
          token: token,
          message: 'Login successful'
        };
      } else {
        throw new Error(responseData.message || 'Login failed');
      }

    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Set authentication with JWT token
   * This should be called after successful login
   */
  setSession(token: string) {
    this.token = token;

    if (typeof window !== 'undefined') {
      const maxAge = 7 * 24 * 60 * 60;
      const isSecure = window.location.protocol === 'https:';
      document.cookie = `auth_token=${token}; path=/; max-age=${maxAge}; SameSite=Lax${isSecure ? '; Secure' : ''}`;
      sessionStorage.setItem('auth_token', token);
    }
  }

  /**
   * Logout user from backend and clear local auth
   */
  async logout() {
    try {
      if (this.token) {
        // Call backend logout if we have authentication
        await fetch(`${this.server_domain}/authenticate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`
          },
          body: JSON.stringify({ action: 'logout' })
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with clearing local auth even if backend call fails
    } finally {
      // Always clear local authentication
      this.clearAuth();
    }
  }

  /**
   * Clear authentication
   */
  clearAuth() {
    this.token = null;

    if (typeof window !== 'undefined') {
      // Clear all cookies
      document.cookie = 'auth_token=; path=/; max-age=0';
      document.cookie = 'user_data=; path=/; max-age=0';

      // Clear entire session storage
      sessionStorage.clear();
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.token;
  }

  /**
   * Modern REST API call for new endpoints
   * @param endpoint API endpoint (e.g., '/vessels', '/health')
   * @param options Fetch options
   */
  async restCall(endpoint: string, options: RequestInit = {}) {
    const url = `${this.api_domain}/${endpoint}`;

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add JWT if available (check cookie first, then sessionStorage, then instance token)
    const token = this.token || (typeof window !== 'undefined' ? (getCookie('auth_token') || sessionStorage.getItem('auth_token')) : null);
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    // Handle error responses
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch {
        // If parsing fails, use default error message
      }

      // Handle 401 Unauthorized - Invalid token
      if (response.status === 401 && errorMessage === 'Invalid token') {
        console.error('[API ERROR] Invalid token - clearing session and logging out');

        // Clear token from memory
        this.token = null;

        // Clear session storage
        if (typeof window !== 'undefined') {
          sessionStorage.clear();

          // Clear auth cookie
          document.cookie = 'auth_token=; path=/; max-age=0';

          // Clear any other auth-related cookies
          document.cookie = 'user_data=; path=/; max-age=0';

          // Redirect to login page
          window.location.href = '/login';
        }

        // Throw error to stop further processing
        const error = new Error('Session expired. Please log in again.') as any;
        error.status = 401;
        throw error;
      }

      const error = new Error(errorMessage) as any;
      error.status = response.status;
      throw error;
    }

    const responseData = await response.json();

    // Handle multiple response formats from Go-API:
    // 1. {success: true, data: {...|null}} - standard format (data may be null for empty results)
    // 2. {data} - direct format (auth endpoints)
    // 3. Any other format - return as-is
    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    // For endpoints that return data directly at root, return as-is
    return responseData;
  }
}

// Export singleton instance
export const apiClient = new APIClient();
export default apiClient;

// Export convenience functions
export const callApi = (module: string, method: string, data: Record<string, unknown> = {}) => {
  return apiClient.callApi(module, method, data);
};

export const login = (email: string, password: string) => {
  return apiClient.login(email, password);
};

export const logout = () => {
  return apiClient.logout();
};

export const setApiAuth = (token: string) => {
  return apiClient.setAuth(token);
};

export const setSession = (token: string) => {
  return apiClient.setSession(token);
};

export const clearApiAuth = () => {
  return apiClient.clearAuth();
};

export const isApiAuthenticated = () => {
  return apiClient.isAuthenticated();
};

// QR Code API functions
export const generateQRCode = (campaignId: string) => {
  return callApi('qr_campaigns', 'generate', { campaignId });
};

export const validateQRCode = (uniqueId: string) => {
  return callApi('qr_usage', 'validate', { uniqueId });
};

export const markQRUsed = (uniqueId: string, customerId: string, playerInfo: any, pointsEarned: number = 0) => {
  return callApi('qr_usage', 'mark_used', {
    uniqueId,
    customerId,
    playerInfo,
    pointsEarned
  });
};

export const getQRCampaigns = (filters: Record<string, unknown> = {}) => {
  return callApi('qr_campaigns', 'list', filters);
};

export const createQRCampaign = (campaignData: any) => {
  return callApi('qr_campaigns', 'create', campaignData);
};

export const updateQRCampaign = (campaignId: string, campaignData: any) => {
  return callApi('qr_campaigns', 'update', { id: campaignId, ...campaignData });
};

export const deleteQRCampaign = (campaignId: string) => {
  return callApi('qr_campaigns', 'delete', { id: campaignId });
};

export const getQRCampaign = (campaignId: string) => {
  return callApi('qr_campaigns', 'read', { id: campaignId });
};

export const checkQRStatus = (uniqueId: string) => {
  return callApi('qr_usage', 'check_status', { uniqueId });
};

// Customer functions for QR flow
export const upsertCustomer = (customerData: any) => {
  return callApi('profile_customer', 'upsert', customerData);
};

export const findCustomerByPhone = (phone: string, merchantId: string) => {
  return callApi('profile_customer', 'find_by_phone', { phone, merchant_id: merchantId });
};

/**
 * List all customers for the authenticated merchant
 */
export const getCustomers = (limit: number = 100, offset: number = 0) => {
  return callApi('customers', 'list', { limit, offset });
};

// Games catalog functions
export const getMerchantGames = () => {
  // merchant_games gets merchant_id from authentication session, not from data
  return callApi('merchant_games', 'list', {});
};

export const getGameDetails = (gameId: string) => {
  return callApi('games_catalog', 'read', { id: gameId });
};

// Public API functions for QR validation and customer operations
// These use callApi but bypass authentication for public endpoints


/**
 * Public QR validation - no auth required
 */
export const publicValidateQRCode = async (uniqueId: string) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/qr-validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        unique_id: uniqueId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();
    // Return full response including success wrapper for qr-register page
    return responseData;
  } catch (error) {
    console.error('Public QR validation error:', error);
    throw error;
  }
};

/**
 * Public customer find by phone - no auth required
 */
export const publicFindCustomerByPhone = async (phone: string, merchantId: string, details?: {
  name?: string;
  email?: string;
  instagram?: string;
}) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/customer-find-by-phone-public`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        phone,
        merchant_id: merchantId,
        ...(details?.name && { name: details.name }),
        ...(details?.email && { email: details.email }),
        ...(details?.instagram && { instagram: details.instagram })
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Handle multiple response formats from Go-API:
    // 1. {success: true, data: {...}} - standard format
    // 2. {data} - direct format (auth endpoints)
    // 3. Any other format - return as-is
    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    // For endpoints that return data directly at root, return as-is
    return responseData;
  } catch (error) {
    console.error('Public customer find error:', error);
    throw error;
  }
};

/**
 * Public customer upsert - no auth required
 */
export const publicUpsertCustomer = async (customerData: {
  merchantId: string;
  name: string;
  phone: string;
  email?: string;
  instagram?: string;
}) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/customer-upsert-public`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        merchant_id: customerData.merchantId,
        name: customerData.name,
        phone: customerData.phone,
        email: customerData.email,
        instagram: customerData.instagram
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Handle multiple response formats from Go-API:
    // 1. {success: true, data: {...}} - standard format
    // 2. {data} - direct format (auth endpoints)
    // 3. Any other format - return as-is
    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    // For endpoints that return data directly at root, return as-is
    return responseData;
  } catch (error) {
    console.error('Public customer upsert error:', error);
    throw error;
  }
};

/**
 * Public QR code mark as used - no auth required
 * Marks a QR code as used by linking it to a customer
 */
export const publicMarkQRUsed = async (data: {
  unique_id: string;
  customer_id: string;
}) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/qr-mark-used`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Handle multiple response formats from Go-API:
    // 1. {success: true, data: {...}} - standard format
    // 2. {data} - direct format (auth endpoints)
    // 3. Any other format - return as-is
    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    // For endpoints that return data directly at root, return as-is
    return responseData;
  } catch (error) {
    console.error('Public QR mark used error:', error);
    throw error;
  }
};

/**
 * Check participation eligibility - no auth required
 * Checks if a customer (by email/phone) can participate in a QR campaign
 */
export const publicCheckParticipation = async (uniqueId: string, email: string, phone: string) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/qr-check-participation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        unique_id: uniqueId,
        email,
        phone
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Handle multiple response formats from Go-API:
    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    return responseData;
  } catch (error) {
    console.error('Public check participation error:', error);
    throw error;
  }
};

/**
 * Record participation - no auth required
 * Records a customer's participation in a QR campaign
 */
export const publicRecordParticipation = async (uniqueId: string, customerId: string, email: string, phone: string) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/qr-record-participation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        unique_id: uniqueId,
        customer_id: customerId,
        email,
        phone
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Handle multiple response formats from Go-API:
    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    return responseData;
  } catch (error) {
    console.error('Public record participation error:', error);
    throw error;
  }
};

/**
 * Public QR status check - no auth required
 */
export const publicCheckQRStatus = async (uniqueId: string) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/qr-check-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uniqueId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Handle multiple response formats from Go-API:
    // 1. {success: true, data: {...}} - standard format
    // 2. {data} - direct format (auth endpoints)
    // 3. Any other format - return as-is
    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    // For endpoints that return data directly at root, return as-is
    return responseData;
  } catch (error) {
    console.error('Public QR status check error:', error);
    throw error;
  }
};

/**
 * Track game completion
 * No auth required as merchant_id is derived from customer
 */
export const publicTrackGameCompletion = async (gameData: {
  customer_id: string;
  game_type: string;
  points_earned: number;
  session_duration?: number;
  score?: number;
  was_completed?: boolean;
  game_catalog_id?: string;
  campaign_id?: string;
  difficulty_level?: string;
  prize_won?: string;
  game_data?: Record<string, any>;
  device_info?: Record<string, any>;
}) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(gameData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Return the full response including success and data properties
    // This ensures the caller can check for result.success
    return responseData;
  } catch (error) {
    console.error('Track game completion error:', error);
    throw error;
  }
};

// Backward compatibility alias if needed, or just replace usage
export const trackGameCompletion = publicTrackGameCompletion;

/**
 * Public games catalog - no auth required
 * Fetches all available games from the catalog
 */
export const publicGetGames = async () => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/games`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Return data from new response format: {success: true, data: [...]}
    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    return responseData;
  } catch (error) {
    console.error('Public games catalog error:', error);
    throw error;
  }
};

/**
 * Public customer get by ID - no auth required
 * Get customer stats including total_points, games_played, etc.
 */
export const publicGetCustomerById = async (customerId: string) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/customer-by-id-public`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customerId
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Return the full response including success and data properties
    return responseData;
  } catch (error) {
    console.error('Public customer get by ID error:', error);
    throw error;
  }
};

/**
 * Public get customer game sessions - no auth required
 * Get all game sessions for a specific customer
 */
export const publicGetCustomerGameSessions = async (customerId: string, limit: number = 50) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/track/sessions/list`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customerId,
        limit: limit
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Return the full response including success and data properties
    return responseData;
  } catch (error) {
    console.error('Public customer game sessions error:', error);
    throw error;
  }
};

/**
 * Get dashboard statistics for the authenticated merchant
 * Requires authentication
 * @returns Promise with dashboard stats: {qr_scans, active_players, games_played, data_points_count}
 */
export const getDashboardStats = async () => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const token = typeof window !== 'undefined' ? (getCookie('auth_token') || sessionStorage.getItem('auth_token')) : null;

    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${baseUrl}/dashboard.stats`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({})
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    // Return data from new response format: {success: true, data: {...}}
    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    return responseData;
  } catch (error) {
    console.error('Dashboard stats error:', error);
    throw error;
  }
};

// ============================================================================
// ACCOUNT API ENDPOINTS
// ============================================================================

/**
 * Get user profile
 * GET /account/profile
 */
export const getAccountProfile = async () => {
  return apiClient.restCall('account/profile');
};

/**
 * Update user profile
 * POST /account/profile.update
 */
export const updateAccountProfile = async (data: {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  avatar_url?: string;
  timezone?: string;
  language?: string;
  business_name?: string;
}) => {
  return apiClient.restCall('account/profile.update', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Upload avatar
 * POST /account/avatar.upload
 */
export const uploadAvatar = async (file: File) => {
  const formData = new FormData();
  formData.append('avatar', file);

  return apiClient.restCall('account/avatar.upload', {
    method: 'POST',
    headers: {
      // Don't set Content-Type for FormData - browser handles it
    },
    body: formData as any,
  });
};

/**
 * Change password
 * POST /account/password.change
 */
export const changePassword = async (data: {
  current_password: string;
  new_password: string;
}) => {
  return apiClient.restCall('account/password.change', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Delete account
 * DELETE /account/delete
 */
export const deleteAccount = async (data: {
  password: string;
  cancel_subscription?: boolean;
}) => {
  return apiClient.restCall('account/delete', {
    method: 'DELETE',
    body: JSON.stringify(data),
  });
};

/**
 * Get usage statistics
 * GET /account/usage
 */
export const getAccountUsage = async (params?: { period_start?: string; period_end?: string }) => {
  const query = new URLSearchParams();
  if (params?.period_start) query.set('period_start', params.period_start);
  if (params?.period_end) query.set('period_end', params.period_end);

  return apiClient.restCall(`account/usage?${query}`);
};

// ============================================================================
// BILLING API ENDPOINTS
// ============================================================================

/**
 * Get subscription details
 * GET /billing/subscription
 */
export const getSubscription = async () => {
  return apiClient.restCall('billing/subscription');
};

/**
 * List available plans
 * GET /billing/plans
 */
export const getBillingPlans = async () => {
  return apiClient.restCall('billing/plans');
};

/**
 * Subscribe to a plan
 * POST /billing/subscription.subscribe
 */
export const subscribeToPlan = async (data: {
  plan_id: string;
  billing_cycle?: 'monthly' | 'yearly';
  payment_method_id?: string;
}) => {
  return apiClient.restCall('billing/subscription.subscribe', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Create a Stripe Checkout session for a new subscription
 * POST /billing/checkout.create
 */
export const createCheckoutSession = async (data: {
  plan_id: string;
  billing_cycle?: 'monthly' | 'yearly';
  success_url?: string;
  cancel_url?: string;
}) => {
  return apiClient.restCall('billing/checkout.create', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Create a free subscription without Stripe checkout
 * POST /billing/subscription.create-free
 */
export const createFreeSubscription = async (data: {
  plan_id: string;
  billing_cycle?: 'monthly' | 'yearly';
}) => {
  return apiClient.restCall('billing/subscription.create-free', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Update subscription
 * POST /billing/subscription.update
 */
export const updateSubscription = async (data: {
  plan_id: string;
  billing_cycle?: 'monthly' | 'yearly';
  proration_behavior?: 'create_prorations' | 'none' | 'always_invoice';
}) => {
  // Don't default to 'create_prorations' - let backend auto-detect
  return apiClient.restCall('billing/subscription.update', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Cancel subscription
 * POST /billing/subscription.cancel
 */
export const cancelSubscription = async () => {
  return apiClient.restCall('billing/subscription.cancel', {
    method: 'POST',
  });
};

/**
 * List payment methods
 * GET /billing/payment-methods
 */
export const getPaymentMethods = async () => {
  return apiClient.restCall('billing/payment-methods');
};

/**
 * Attach payment method
 * POST /billing/payment-methods.attach
 */
export const attachPaymentMethod = async (data: { payment_method_id: string }) => {
  return apiClient.restCall('billing/payment-methods.attach', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Set default payment method
 * POST /billing/payment-methods/:id/set-default
 */
export const setDefaultPaymentMethod = async (id: string) => {
  return apiClient.restCall(`billing/payment-methods/${id}/set-default`, {
    method: 'POST',
  });
};

/**
 * Detach payment method
 * DELETE /billing/payment-methods/:id
 */
export const detachPaymentMethod = async (id: string) => {
  return apiClient.restCall(`billing/payment-methods/${id}`, {
    method: 'DELETE',
  });
};

/**
 * Create setup intent for adding a payment method
 * POST /billing/setup-intent
 */
export const createSetupIntent = async () => {
  return apiClient.restCall('billing/setup-intent', {
    method: 'POST',
  });
};

/**
 * List invoices
 * GET /billing/invoices
 */
export const getInvoices = async () => {
  return apiClient.restCall('billing/invoices');
};

/**
 * Get invoice details
 * GET /billing/invoices/:id
 */
export const getInvoice = async (id: string) => {
  return apiClient.restCall(`billing/invoices/${id}`);
};

/**
 * Get upcoming invoice with scheduled plan changes
 * GET /billing/invoices/upcoming
 */
export const getUpcomingInvoice = async () => {
  return apiClient.restCall('billing/invoices/upcoming');
};

/**
 * Retry payment for past-due subscription
 * POST /billing/invoice.retry
 */
export const retryInvoicePayment = async (data?: {
  payment_method_id?: string;
}) => {
  return apiClient.restCall('billing/invoice.retry', {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
};

// ============================================================================
// EMAIL VERIFICATION API ENDPOINTS
// ============================================================================

/**
 * Send verification email
 * POST /verify/send
 */
export const sendVerificationEmail = async (email: string) => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/verify/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    if (responseData && responseData.success === true && 'data' in responseData) {
      return responseData.data;
    }

    return responseData;
  } catch (error) {
    console.error('Send verification email error:', error);
    throw error;
  }
};

/**
 * Verify OTP code
 * POST /verify/verify
 */
export const verifyOTP = async (email: string, code: string): Promise<{ verified: boolean; user_id?: string; message?: string }> => {
  try {
    const baseUrl = (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8080/v1').replace(/\/$/, '');
    const response = await fetch(`${baseUrl}/verify/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, code }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Verify OTP error:', error);
    throw error;
  }
};

// ============================================================================
// COMPANY INFO API ENDPOINTS
// ============================================================================

/**
 * Get company info
 * GET /account/company-info
 */
export const getCompanyInfo = async () => {
  return apiClient.restCall('account/company-info');
};

/**
 * Update company info
 * POST /account/company-info.update
 */
export const updateCompanyInfo = async (data: {
  business_name?: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  website?: string;
  industry?: string;
  description?: string;
  tax_id?: string;
  facebook?: string;
  instagram?: string;
  whatsapp?: string;
  telegram?: string;
  timezone?: string;
}) => {
  return apiClient.restCall('account/company-info.update', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};